import { useEffect, useRef } from 'react';

interface LandingFrameProps {
    html: string;
    title: string;
}

const stripDocumentTags = (html: string): string =>
    html
        .replace(/<!DOCTYPE[^>]*>/i, '')
        .replace(/<\/?html[^>]*>/gi, '')
        .replace(/<\/?head[^>]*>/gi, '')
        .replace(/<\/?body[^>]*>/gi, '');

const LandingFrame = ({ html, title }: LandingFrameProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const prevTitle = document.title;
        document.title = title;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.title = prevTitle;
            document.body.style.overflow = prevOverflow;
        };
    }, [title]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        container.innerHTML = stripDocumentTags(html);

        const scripts = Array.from(container.querySelectorAll('script'));
        scripts.forEach((oldScript) => {
            const newScript = document.createElement('script');
            for (const attr of Array.from(oldScript.attributes)) {
                newScript.setAttribute(attr.name, attr.value);
            }
            newScript.text = oldScript.textContent ?? '';
            oldScript.parentNode?.replaceChild(newScript, oldScript);
        });

        // 내부 스크롤 컨테이너 안에서는 IntersectionObserver(root=viewport)가
        // 화면 밖 섹션을 감지 못 하므로, .f-reveal 요소를 직접 노출시킨다.
        const reveal = () => {
            container.querySelectorAll('.f-reveal').forEach((el) => el.classList.add('is-in'));
        };
        const rafId = requestAnimationFrame(reveal);

        return () => {
            cancelAnimationFrame(rafId);
            container.innerHTML = '';
        };
    }, [html]);

    return (
        <div
            ref={containerRef}
            style={{
                position: 'fixed',
                inset: 0,
                width: '100vw',
                height: '100vh',
                overflow: 'auto',
                background: '#fff',
                zIndex: 2000,
            }}
        />
    );
};

export default LandingFrame;
